import type { ScalarMetricKey, SeriesBucket, SeriesMetricKey } from '@/types/analyticsV2';

/**
 * Single source of truth for everything the UI needs to know about a metric:
 * its label, what its card shows, and (when a backend series exists) how
 * its trend chart renders. Adding a new metric = one entry here.
 *
 * Display knowledge lives entirely on the frontend; the backend just returns
 * uniform numbers per bucket (count, total, percent). The chart picks which
 * fields to plot from `chartSeries`.
 */

export type MetricCardDisplay = 'count-with-denominator' | 'percent';
export type ChartType = 'line' | 'area';
export type ValueFormat = 'number' | 'percent';

export interface ChartSeriesDef {
  field: keyof Pick<SeriesBucket, 'count' | 'total' | 'percent'>;
  label: string;
  color: string;
}

export interface MetricDef {
  key: ScalarMetricKey;
  label: string;
  description: string;

  cardDisplay: MetricCardDisplay;
  /**
   * Card-side trend selector. Series-aware metrics still use the scalar
   * trend on the card; the chart shows the bucketed series itself.
   */
  trendField: 'countChangePct' | 'percentPointChange';

  chart?: {
    seriesKey: SeriesMetricKey;
    type: ChartType;
    yFormat: ValueFormat;
    yDomain?: [number, number];
    series: ChartSeriesDef[];
  };
}

const lavender = '#7C3AED';
const grey = '#9CA3AF';

export const metricDefs: Record<ScalarMetricKey, MetricDef> = {
  aiMessages: {
    key: 'aiMessages',
    label: 'AI Messages',
    description: 'Count of outgoing messages generated by AI.',
    cardDisplay: 'count-with-denominator',
    trendField: 'countChangePct',
    chart: {
      seriesKey: 'aiMessages',
      type: 'area',
      yFormat: 'number',
      series: [
        { field: 'count', label: 'AI-generated', color: lavender },
        { field: 'total', label: 'Total outgoing', color: grey },
      ],
    },
  },
  aiAcceptanceRate: {
    key: 'aiAcceptanceRate',
    label: 'AI Acceptance Rate',
    description: 'Suggestions added to the composer.',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
    chart: {
      seriesKey: 'aiAcceptanceRate',
      type: 'area',
      yFormat: 'percent',
      series: [
        { field: 'percent', label: 'Acceptance rate', color: lavender },
      ],
    },
  },
  aiCoverage: {
    key: 'aiCoverage',
    label: 'AI Coverage',
    description: 'Share of conversations where AI helped respond.',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
    chart: {
      seriesKey: 'aiCoverage',
      type: 'area',
      yFormat: 'percent',
      series: [
        { field: 'percent', label: 'Coverage', color: lavender },
      ],
    },
  },
  aiAutomation: {
    key: 'aiAutomation',
    label: 'AI Automation',
    description: 'Share of conversations where AI auto-replied at least once.',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
    chart: {
      seriesKey: 'aiAutomation',
      type: 'area',
      yFormat: 'percent',
      series: [
        { field: 'percent', label: 'Automation', color: lavender },
      ],
    },
  },
  aiAccuracy: {
    key: 'aiAccuracy',
    label: 'Generated Accuracy',
    description: 'Of QA-scored AI responses (any confidence), the share rated Perfect or Good Enough.',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
    chart: {
      seriesKey: 'aiAccuracy',
      type: 'area',
      yFormat: 'percent',
      series: [
        { field: 'percent', label: 'Accuracy', color: lavender },
      ],
    },
  },
  aiEdits: {
    key: 'aiEdits',
    label: 'AI Edits',
    description: 'AI-influenced messages with light edits (under 20% changed).',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
    chart: {
      seriesKey: 'aiEdits',
      type: 'area',
      yFormat: 'percent',
      series: [
        { field: 'percent', label: 'Light edits', color: lavender },
      ],
    },
  },
  // Ticket Metrics tab only. Not iterated by OverviewSection (it's absent
  // from SCALAR_METRIC_KEYS). Lives here so `Record<ScalarMetricKey, MetricDef>`
  // stays exhaustive and consumers like `ChartMetricSelector` can ignore it
  // safely via `isMetricSeriesAvailable`.
  aiTicketCoverage: {
    key: 'aiTicketCoverage',
    label: 'Tickets Handled by AI',
    description: 'Distinct CS conversations where AI helped at any point (grouped by conversation).',
    cardDisplay: 'count-with-denominator',
    trendField: 'countChangePct',
  },
  avgAiResponsesPerTicket: {
    key: 'avgAiResponsesPerTicket',
    label: 'Avg AI responses /ticket',
    description: 'Avg AI responses per conversation in the period.',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
  },
  // Coverage Funnel section on the super-admin page only. Absent from
  // SCALAR_METRIC_KEYS / SERIES_METRIC_KEYS, so no public card and no chart;
  // they live here to keep `Record<ScalarMetricKey, MetricDef>` exhaustive.
  aiSurfacedAccuracy: {
    key: 'aiSurfacedAccuracy',
    label: 'Surfaced Accuracy',
    description: 'Of QA-scored AI responses surfaced (confidence ≥ 0.5), the share rated Perfect or Good Enough.',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
  },
  aiGeneratedCoverage: {
    key: 'aiGeneratedCoverage',
    label: 'Generated Coverage (Suggested response - HITL Pending)',
    description: 'Share of CS tickets whose reply carried an AI response (any confidence).',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
  },
  aiSurfacedCoverage: {
    key: 'aiSurfacedCoverage',
    label: 'Surfaced Coverage',
    description: 'Share of CS tickets with a surfaced AI response (confidence ≥ 0.5).',
    cardDisplay: 'percent',
    trendField: 'percentPointChange',
  },
};

export const isMetricSeriesAvailable = (key: ScalarMetricKey): boolean =>
  metricDefs[key]?.chart !== undefined;
